/**
 * Where the project architecture's lines run, as plain geometry: the picture
 * is laid out by the browser (bands, groups and cards that wrap), measured,
 * and the lines are then drawn over it by these rules. DOM-free, so the
 * rules are tested without a browser (test/projectArchEdges.test.ts).
 *
 * A line may only run through free space. Every card and every piece of a
 * repository's heading is an obstacle; what is left is a small grid of lanes:
 *
 *   across   the gap between two bands (a "strip"), the margin above the
 *            first band and below the last, and — inside a band whose groups
 *            or cards wrap — the gap between two rows of them
 *   down     wherever a row of cards and headings leaves a column clear: the
 *            picture's side margins, the gutter between two repositories,
 *            the gap between two cards
 *
 * A line leaves its source on the side facing its target — the bottom or top
 * when they are in different bands, the left or right when they share one —
 * runs along lanes, and arrives square on the target's side. It goes straight
 * where a column is clear all the way; where a card in a band between is in
 * the way it goes round by the nearest clear column; and where the way
 * straight out of an end is itself behind a card (the second row of a group)
 * it takes the column beside the card and leaves by that side instead. A
 * whole repository's end is its group's box.
 *
 * No two lines lie on each other. The ends on one side of a card fan out
 * along it, in the order of where their lines go; the lines sharing a column
 * each take an offset of their own; and the runs in one strip each take a
 * track, a label high when the caller asks, so every label sits on its own
 * line in the gap between two bands, clear of every card and of every other
 * label. `loads` says how many tracks each gap holds, and the picture gives
 * the gap that room; which way a line goes never depends on how much room a
 * strip has, so the two settle at once. A line with no run in a strip (two
 * things in one band) carries its label where it fits, the small one where
 * the words do not.
 *
 * Everything is decided by the rectangles alone, in a fixed order: the same
 * picture always gives the same lines. The cost is one pass over the rows
 * between a line's ends, by the lanes of the picture, per line.
 */

export interface EdgeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EdgePoint {
  x: number;
  y: number;
}

export interface EdgeSize {
  width: number;
  height: number;
}

/** The picture as measured: everything in one coordinate space. */
export interface EdgeScene {
  width: number;
  height: number;
  /** The bands, in any order. */
  bands: readonly EdgeRect[];
  /** Each repository's box in a band. A line may cross one, but never runs along its border. */
  groups?: readonly EdgeRect[];
  /** What no line may pass behind: every card, and each piece of a group's heading. */
  obstacles: readonly EdgeRect[];
}

export interface EdgeRequest {
  id: string;
  /** What the ends are, so lines sharing one can fan out. */
  fromId: string;
  toId: string;
  /** A card (one of the obstacles) or a group's box (which holds some). */
  from: EdgeRect;
  to: EdgeRect;
  /** The size of the label the line carries. Left out, it carries none. */
  label?: EdgeSize;
}

export type EdgeSide = 'top' | 'bottom' | 'left' | 'right';

export interface EdgeLabel {
  /** Its middle. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** No room for the words: the small size was used. */
  compact: boolean;
}

export interface EdgeRoute {
  id: string;
  /** The SVG path. */
  d: string;
  /** The corners, ends included. */
  points: EdgePoint[];
  /** The side of its source it leaves by, and the side of its target it arrives at. */
  from: EdgeSide;
  to: EdgeSide;
  label?: EdgeLabel;
}

export interface EdgeRouting {
  routes: Map<string, EdgeRoute>;
  /**
   * How many tracks each gap holds: `[0]` is the margin above the first band,
   * `[i]` the strip between band `i - 1` and band `i` (top to bottom), and the
   * last the margin below the last band.
   */
  loads: number[];
}

export interface RouteOptions {
  /**
   * How far apart two lines turning in one strip run. A caller that gives
   * each track the height of a label (and the strip the room, `stripHeight`)
   * gets labels that never sit on each other.
   */
  laneStep?: number;
  /** The size of a label with no room for its words. */
  compact?: EdgeSize;
  /** Shifts every lane a little, for a second set of lines drawn over the first so the two do not coincide. */
  bias?: number;
}

/** A line keeps this far from a card or a heading it passes… */
const CLEAR = 4;
/** …and this far from the border of a band or of a repository's box it would otherwise run along. */
const BORDER = 3;
/** Nothing is drawn nearer the picture's own edge than this. */
const EDGE = 2;
/** An end keeps this far off the corners of the side it is on. */
const INSET = 8;
/** Between two lines in one column. */
const STEP = 5;
/** Between two ends on one side of the same thing: room for an arrowhead each. */
const FAN_STEP = 14;
/** Between two lines turning in one strip, unless the caller makes room for more (a label per track). */
const LANE_STEP = 7;
/** Two runs may share a track when they are at least this far apart along it: nearer, one would read as the other going on. */
const TRACK_GAP = 20;
/** Two rows of cards nearer than this have no lane between them: they count as one row. */
const MIN_CORRIDOR = 2 * CLEAR;
/** A clear column narrower than this holds no line. */
const MIN_LANE = 6;
/** A clear column up to this wide has one lane, down its middle; a wider one has a lane near each side. */
const WIDE = 48;
const WIDE_PAD = 9;
/** The first track in the margin above the first band (or below the last) is this far from the band. */
const EDGE_GAP = 16;
/** A line shows at least this much either side of a label that sits on it. */
const SHOW = 10;
/** The small label: the kind's icon alone. */
const COMPACT: EdgeSize = { width: 20, height: 20 };

// What a route costs, in pixels of detour. A jog is two corners; the rest say which of two clear ways is the tidier.
const JOG = 60;
/** Travel in the gap between two rows of one band, and a jog there: tight, no home for a label, and dearer than leaving by the side. */
const INNER_WEIGHT = 3;
const INNER_JOG = 150;
/** An end away from the middle of its side, per pixel. */
const OFF_CENTRE = 1.2;
/** Leaving by the side when the bands differ: only when the way straight out is behind a card (more than any saving in distance, less than a jog between two rows of cards). */
const SIDE_EXIT = 200;
/** A column inside a repository's box, between two of its cards, rather than a gutter. */
const INSIDE_LANE = 12;
/** The picture's outer margin, outside the bands. */
const OUTER_LANE = 8;
/** A column that already holds as many lines as fit, for each line over: worth a fair way round, so a narrow gap is not asked to hold a bundle. */
const CROWDED = 150;
/** No clear way at all: the line still gets drawn. */
const BLOCKED = 100_000;
/** Between equals, the lane nearer the picture's middle. */
const CENTRE_TIE = 0.002;
/** The height a strip counts for when two ways round are compared, so the answer does not change as strips grow. */
const STRIP_NOMINAL = 40;

const right = (r: EdgeRect) => r.x + r.width;
const bottom = (r: EdgeRect) => r.y + r.height;
const cx = (r: EdgeRect) => r.x + r.width / 2;
const cy = (r: EdgeRect) => r.y + r.height / 2;
const holds = (outer: EdgeRect, inner: EdgeRect) => inner.x >= outer.x - 0.5 && inner.y >= outer.y - 0.5 && right(inner) <= right(outer) + 0.5 && bottom(inner) <= bottom(outer) + 0.5;

/** An orthogonal polyline with its corners rounded, as flowViaPath rounds them. */
export function roundedPath(points: readonly EdgePoint[], radius = 6): string {
  const corners = points
    .filter((p, i, all) => i === 0 || p.x !== all[i - 1].x || p.y !== all[i - 1].y)
    .filter((p, i, all) => i === 0 || i === all.length - 1 || !((all[i - 1].x === p.x && p.x === all[i + 1].x) || (all[i - 1].y === p.y && p.y === all[i + 1].y)));
  if (corners.length === 0) return '';
  let d = `M ${corners[0].x} ${corners[0].y}`;
  for (let i = 1; i < corners.length - 1; i++) {
    const [a, p, b] = [corners[i - 1], corners[i], corners[i + 1]];
    const inLen = Math.hypot(p.x - a.x, p.y - a.y);
    const outLen = Math.hypot(b.x - p.x, b.y - p.y);
    const r = Math.min(radius, inLen / 2, outLen / 2);
    if (r <= 0) continue;
    const before = { x: p.x - ((p.x - a.x) / inLen) * r, y: p.y - ((p.y - a.y) / inLen) * r };
    const after = { x: p.x + ((b.x - p.x) / outLen) * r, y: p.y + ((b.y - p.y) / outLen) * r };
    d += ` L ${before.x} ${before.y} Q ${p.x} ${p.y} ${after.x} ${after.y}`;
  }
  if (corners.length > 1) {
    const end = corners[corners.length - 1];
    d += ` L ${end.x} ${end.y}`;
  }
  return d;
}

// ── Stretches of a line ────────────────────────────────────────────────

interface Span {
  lo: number;
  hi: number;
}

const mid = (s: Span) => (s.lo + s.hi) / 2;

/** `spans` without what lies between `lo` and `hi`. */
function cut(spans: readonly Span[], lo: number, hi: number): Span[] {
  const out: Span[] = [];
  for (const s of spans) {
    if (hi <= s.lo || lo >= s.hi) {
      out.push(s);
      continue;
    }
    if (lo > s.lo) out.push({ lo: s.lo, hi: lo });
    if (hi < s.hi) out.push({ lo: hi, hi: s.hi });
  }
  return out;
}

/** What of `spans` lies inside `within`. */
function clip(spans: readonly Span[], within: Span): Span[] {
  const out: Span[] = [];
  for (const s of spans) {
    const lo = Math.max(s.lo, within.lo);
    const hi = Math.min(s.hi, within.hi);
    if (lo <= hi) out.push({ lo, hi });
  }
  return out;
}

function spanAt<T extends Span>(spans: readonly T[], at: number): T | undefined {
  return spans.find((s) => at >= s.lo - 1e-6 && at <= s.hi + 1e-6);
}

function widest(spans: readonly Span[]): Span | undefined {
  let best: Span | undefined;
  for (const s of spans) if (!best || s.hi - s.lo > best.hi - best.lo) best = s;
  return best;
}

// ── The grid of lanes ──────────────────────────────────────────────────

/** A clear column through one row. */
interface Piece extends Span {
  /** Between two cards of one repository, inside its box. */
  inside: boolean;
  /** In the picture's margin, outside the bands. */
  outer: boolean;
}

/** A row of obstacles: no line crosses it but by one of its clear columns. */
interface Row {
  band: number;
  top: number;
  bottom: number;
  pieces: Piece[];
}

/** The clear stretch above a row (and, for the last, below the last row): a line may run any distance along it. */
interface Corridor {
  kind: 'strip' | 'inner' | 'top' | 'bottom';
  /** Where its tracks go. */
  top: number;
  bottom: number;
  /** Its place in `loads`; -1 for one inside a band. */
  gap: number;
  /** Its height in a picture whose strips never grow: what "nearer" is measured in. */
  level: number;
}

/** The lanes of one picture. Build it once per measurement (`edgeGrid`) and route any number of sets of lines over it. */
export interface EdgeGrid {
  readonly width: number;
  readonly height: number;
  readonly bands: readonly EdgeRect[];
  readonly obstacles: readonly EdgeRect[];
  readonly rows: readonly Row[];
  readonly corridors: readonly Corridor[];
  /** Every lane's x, in order. */
  readonly lanes: readonly number[];
  /** Each band's top in a picture whose strips never grow. */
  readonly levels: readonly number[];
}

function bandOf(bands: readonly EdgeRect[], r: EdgeRect): number {
  const at = cy(r);
  let best = 0;
  let bestGap = Infinity;
  bands.forEach((b, i) => {
    const gap = at < b.y ? b.y - at : at > bottom(b) ? at - bottom(b) : 0;
    if (gap < bestGap) [best, bestGap] = [i, gap];
  });
  return best;
}

export function edgeGrid(scene: EdgeScene): EdgeGrid {
  const { width, height } = scene;
  const bands = scene.bands.length ? [...scene.bands].sort((a, b) => a.y - b.y || a.x - b.x) : [{ x: 0, y: 0, width, height }];
  const groups = scene.groups ?? [];
  const perBand: EdgeRect[][] = bands.map(() => []);
  for (const o of scene.obstacles) perBand[bandOf(bands, o)].push(o);

  const rows: Row[] = [];
  const members: EdgeRect[][] = [];
  bands.forEach((band, b) => {
    const sorted = [...perBand[b]].sort((p, q) => p.y - q.y || p.x - q.x);
    // A band with nothing in it is still crossed: one row, clear all the way along.
    if (!sorted.length) {
      rows.push({ band: b, top: cy(band), bottom: cy(band), pieces: [] });
      members.push([]);
      return;
    }
    let row: Row | undefined;
    for (const o of sorted) {
      if (row && o.y < row.bottom + MIN_CORRIDOR) {
        row.bottom = Math.max(row.bottom, bottom(o));
        members[members.length - 1].push(o);
      } else {
        row = { band: b, top: o.y, bottom: bottom(o), pieces: [] };
        rows.push(row);
        members.push([o]);
      }
    }
  });

  const lanes = new Set<number>();
  rows.forEach((row, r) => {
    const band = bands[row.band];
    let free: Span[] = [{ lo: EDGE, hi: width - EDGE }];
    for (const o of members[r]) free = cut(free, o.x - CLEAR, right(o) + CLEAR);
    const boxes = groups.filter((g) => g.y < row.bottom && bottom(g) > row.top);
    for (const at of [band.x, right(band), ...boxes.flatMap((g) => [g.x, right(g)])]) free = cut(free, at - BORDER, at + BORDER);
    row.pieces = free
      .filter((s) => s.hi - s.lo >= MIN_LANE)
      .map((s) => ({ ...s, inside: boxes.some((g) => mid(s) > g.x && mid(s) < right(g)), outer: mid(s) < band.x || mid(s) > right(band) }));
    for (const p of row.pieces) for (const x of p.hi - p.lo <= WIDE ? [mid(p)] : [p.lo + WIDE_PAD, p.hi - WIDE_PAD]) lanes.add(x);
  });

  // Heights as they would be with every strip at its least: the same whatever room the picture has since given a strip.
  const levels: number[] = [];
  bands.forEach((_band, i) => levels.push(i === 0 ? 0 : levels[i - 1] + bands[i - 1].height + STRIP_NOMINAL));
  const corridors: Corridor[] = [];
  for (let k = 0; k <= rows.length; k++) {
    const above = rows[k - 1];
    const below = rows[k];
    if (!above) corridors.push({ kind: 'top', top: 0, bottom: bands[0].y, gap: 0, level: -STRIP_NOMINAL / 2 });
    else if (!below) {
      const last = bands[bands.length - 1];
      corridors.push({ kind: 'bottom', top: bottom(last), bottom: Math.max(height, bottom(last)), gap: bands.length, level: levels[bands.length - 1] + last.height + STRIP_NOMINAL / 2 });
    } else if (above.band !== below.band) corridors.push({ kind: 'strip', top: bottom(bands[above.band]), bottom: bands[below.band].y, gap: below.band, level: levels[below.band] - STRIP_NOMINAL / 2 });
    else {
      // Between two rows of one band: the widest stretch that runs along no box's border.
      let zone: Span[] = [{ lo: above.bottom + CLEAR, hi: below.top - CLEAR }];
      for (const g of groups) for (const at of [g.y, bottom(g)]) zone = cut(zone, at - BORDER, at + BORDER);
      const best = widest(zone) ?? { lo: (above.bottom + below.top) / 2, hi: (above.bottom + below.top) / 2 };
      corridors.push({ kind: 'inner', top: best.lo, bottom: best.hi, gap: -1, level: levels[above.band] + mid(best) - bands[above.band].y });
    }
  }
  return { width, height, bands, obstacles: scene.obstacles, rows, corridors, lanes: [...lanes].sort((a, b) => a - b), levels };
}

/** The columns clear of every obstacle between two heights. `part` is an end of the line: what it holds is not in its own way. */
function freeBetween(grid: EdgeGrid, y0: number, y1: number, part: EdgeRect): Span[] {
  let free: Span[] = [{ lo: EDGE, hi: grid.width - EDGE }];
  for (const o of grid.obstacles) if (o.y < y1 && bottom(o) > y0 && !holds(part, o)) free = cut(free, o.x - CLEAR, right(o) + CLEAR);
  return free;
}

/** The rows a rectangle stands in. With none (nothing measured there), `first` is the row below it and `last` the one above. */
function rowsOf(grid: EdgeGrid, r: EdgeRect): { first: number; last: number } {
  let first = -1;
  let last = -1;
  grid.rows.forEach((row, i) => {
    if (row.top < bottom(r) && row.bottom > r.y) {
      if (first < 0) first = i;
      last = i;
    }
  });
  if (first >= 0) return { first, last };
  const below = grid.rows.findIndex((row) => row.top >= bottom(r));
  first = below < 0 ? grid.rows.length : below;
  return { first, last: first - 1 };
}

// ── How an end is reached ──────────────────────────────────────────────

/** The ways out of one end, up or down. */
interface Reach {
  end: EdgeRect;
  dir: -1 | 1;
  /** The corridor it comes out into. */
  corridor: number;
  /** What of the facing side has a clear way straight out. */
  straight: Span[];
  /** Where on that side an end sits when nothing pulls it elsewhere. */
  prefer: number;
  /** What of it is clear all the way out of the band. */
  through: Span[];
  /** The clear columns beside it, reached from its left and right sides. */
  left: Lane[];
  right: Lane[];
}

/** A stretch of one of a row's clear columns. */
interface Lane extends Span {
  piece?: Piece;
}

/** What a column costs a line: a gutter is the tidiest, and one already holding as many lines as fit is the last resort. */
function price(piece: Piece, usage: ReadonlyMap<Piece, number>): number {
  const room = Math.floor((piece.hi - piece.lo) / STEP) + 1;
  return (piece.inside ? INSIDE_LANE : 0) + (piece.outer ? OUTER_LANE : 0) + CROWDED * Math.max(0, (usage.get(piece) ?? 0) - room + 1);
}

function reach(grid: EdgeGrid, end: EdgeRect, dir: -1 | 1): Reach {
  const { first, last } = rowsOf(grid, end);
  const has = last >= first;
  const band = grid.bands[bandOf(grid.bands, end)];
  const side: Span = end.width > 2 * INSET ? { lo: end.x + INSET, hi: right(end) - INSET } : { lo: cx(end), hi: cx(end) };
  const rowEdge = dir < 0 ? (has ? Math.min(grid.rows[first].top, end.y) : end.y) : has ? Math.max(grid.rows[last].bottom, bottom(end)) : bottom(end);
  const straight = clip(dir < 0 ? freeBetween(grid, rowEdge, end.y, end) : freeBetween(grid, bottom(end), rowEdge, end), side);
  const through = clip(dir < 0 ? freeBetween(grid, Math.min(band.y, end.y), end.y, end) : freeBetween(grid, bottom(end), Math.max(bottom(band), bottom(end)), end), side);
  const prefer = mid(widest(through) ?? widest(straight) ?? side);

  // Beside it: the column next to each side that is clear from the end's own height out to the corridor.
  const beside = cut(dir < 0 ? freeBetween(grid, rowEdge, bottom(end), end) : freeBetween(grid, end.y, rowEdge, end), end.x - CLEAR, right(end) + CLEAR);
  const leftOf = beside.find((s) => Math.abs(s.hi - (end.x - CLEAR)) < 0.01);
  const rightOf = beside.find((s) => Math.abs(s.lo - (right(end) + CLEAR)) < 0.01);
  let pieces: readonly Piece[] | undefined;
  if (has) {
    let at = first;
    for (let r = first; r <= last; r++) if (Math.abs((grid.rows[r].top + grid.rows[r].bottom) / 2 - cy(end)) < Math.abs((grid.rows[at].top + grid.rows[at].bottom) / 2 - cy(end))) at = r;
    pieces = grid.rows[at].pieces;
  }
  const lanesIn = (s: Span | undefined): Lane[] => {
    if (!s) return [];
    if (!pieces) return [s];
    return pieces.flatMap((piece) => (Math.max(piece.lo, s.lo) <= Math.min(piece.hi, s.hi) ? [{ lo: Math.max(piece.lo, s.lo), hi: Math.min(piece.hi, s.hi), piece }] : []));
  };
  return { end, dir, corridor: dir < 0 ? first : last + 1, straight, prefer, through, left: lanesIn(leftOf), right: lanesIn(rightOf) };
}

/** One way out of an end: the column it takes and the side it leaves by. */
interface Exit {
  x: number;
  cost: number;
  side: EdgeSide;
  /** How far the column may be moved and still be clear. */
  span: Span;
  /** The column beside the end, for one that leaves by a side. */
  piece?: Piece;
}

/** The ways out at each lane (`xs`), by the lane's index. */
function exits(r: Reach, xs: readonly number[], mode: { straight: boolean; beside: boolean; besideCost: number }, usage: ReadonlyMap<Piece, number>): Array<Exit | undefined> {
  const lane = (l: Lane) => (l.piece ? price(l.piece, usage) : 0);
  let any = false;
  const out = xs.map((x): Exit | undefined => {
    const straight = mode.straight ? spanAt(r.straight, x) : undefined;
    if (straight) return { x, cost: OFF_CENTRE * Math.abs(x - r.prefer), side: r.dir < 0 ? 'top' : 'bottom', span: straight };
    if (!mode.beside) return undefined;
    // Where both ways are open, the side costs its price and the distance from where the end would have been: never the cheaper for being nearer.
    const left = spanAt(r.left, x);
    if (left) return { x, cost: mode.besideCost + lane(left) + (mode.straight ? Math.abs(x - r.prefer) : r.end.x - x), side: 'left', span: left, piece: left.piece };
    const beside = spanAt(r.right, x);
    if (beside) return { x, cost: mode.besideCost + lane(beside) + (mode.straight ? Math.abs(x - r.prefer) : x - right(r.end)), side: 'right', span: beside, piece: beside.piece };
    return undefined;
  });
  for (const e of out) if (e) any = true;
  if (!any) {
    // Hemmed in on every side: straight out all the same, so the line is at least drawn.
    const at = xs.indexOf(cx(r.end));
    if (at >= 0) out[at] = { x: xs[at], cost: BLOCKED, side: r.dir < 0 ? 'top' : 'bottom', span: { lo: xs[at], hi: xs[at] } };
  }
  return out;
}

// ── Finding the way ────────────────────────────────────────────────────

/** Along a corridor to another lane (or straight across it), or through a row by one of its clear columns. */
type Step = { turn: number; must?: boolean } | { pass: number };

interface Search {
  grid: EdgeGrid;
  /** A line with a label goes round by a strip, where the label fits, never by a gap inside a band. */
  labelled: boolean;
  /** Going round something in its own band, a line with no label may as well turn in the gap between two rows. */
  roomy?: boolean;
  /** How many lines each column already holds. */
  usage: Map<Piece, number>;
}

/** The cheapest way from one of `start` to one of `end` by `steps`: its cost and the lane (index into `xs`) it is on before the first step and after each. */
function search(ctx: Search, xs: readonly number[], start: ReadonlyArray<Exit | undefined>, steps: readonly Step[], end: ReadonlyArray<Exit | undefined>): { cost: number; at: number[] } | undefined {
  const n = xs.length;
  let cur = new Float64Array(n);
  for (let j = 0; j < n; j++) cur[j] = start[j]?.cost ?? Infinity;
  const back: Array<Int32Array | undefined> = [];
  for (const step of steps) {
    const next = new Float64Array(n);
    if ('pass' in step) {
      const row = ctx.grid.rows[step.pass];
      let any = false;
      for (let j = 0; j < n; j++) {
        const piece = cur[j] < Infinity ? spanAt(row.pieces, xs[j]) : undefined;
        if (!piece) {
          next[j] = Infinity;
          continue;
        }
        any = true;
        next[j] = cur[j] + price(piece, ctx.usage) + CENTRE_TIE * Math.abs(xs[j] - ctx.grid.width / 2);
      }
      // A row with no clear column at all (it has no pieces): behind it, rather than no line.
      if (!any) for (let j = 0; j < n; j++) next[j] = cur[j] + BLOCKED;
      back.push(undefined);
    } else {
      const corridor = ctx.grid.corridors[step.turn];
      const inner = corridor.kind === 'inner' && !ctx.roomy;
      const weight = inner ? INNER_WEIGHT : 1;
      const jog = JOG + (inner ? INNER_JOG : 0);
      const from = new Int32Array(n);
      for (let j = 0; j < n; j++) {
        next[j] = step.must ? Infinity : cur[j];
        from[j] = j;
      }
      for (const dir of [1, -1]) {
        let best = Infinity;
        let source = -1;
        for (let j = dir > 0 ? 0 : n - 1; j >= 0 && j < n; j += dir) {
          if (source >= 0) {
            best += weight * Math.abs(xs[j] - xs[j - dir]);
            if (best < next[j]) {
              next[j] = best;
              from[j] = source;
            }
          }
          if (cur[j] + jog < best) {
            best = cur[j] + jog;
            source = j;
          }
        }
      }
      back.push(from);
    }
    cur = next;
  }
  let last = -1;
  let cost = Infinity;
  for (let j = 0; j < n; j++) {
    const total = end[j] ? cur[j] + end[j]!.cost : Infinity;
    if (total < cost) [last, cost] = [j, total];
  }
  if (last < 0) return undefined;
  const at = new Array<number>(steps.length + 1);
  at[steps.length] = last;
  for (let s = steps.length - 1; s >= 0; s--) at[s] = back[s] ? back[s]![at[s + 1]] : at[s + 1];
  return { cost, at };
}

/** One straight stretch of a line. */
interface Leg {
  axis: 'v' | 'h';
  /** Where it would like to be (x for a vertical one, y for a horizontal), how far it may move, and where it ended up. */
  want: number;
  lo: number;
  hi: number;
  pos: number;
  /** A horizontal leg's corridor; none for one inside a row (out of the side of an end, or straight across to the other). */
  corridor?: number;
  /** The corridors a vertical leg goes straight across. */
  across: number[];
  /** The side of an end it is on: `<end>|<side>`. */
  ports: string[];
  /** Filled in once every line is planned. */
  span: Span;
  far: number;
}

interface Plan {
  req: EdgeRequest;
  legs: Leg[];
  from: EdgeSide;
  to: EdgeSide;
}

const leg = (axis: 'v' | 'h', want: number, lo: number, hi: number, corridor?: number): Leg => ({ axis, want, lo: Math.min(lo, want), hi: Math.max(hi, want), pos: want, ...(corridor !== undefined ? { corridor } : {}), across: [], ports: [], span: { lo: 0, hi: 0 }, far: want });

/** The short run out of the side of an end to the column beside it. */
function stub(end: EdgeRect): Leg {
  const tight = end.height <= 2 * INSET;
  return leg('h', cy(end), tight ? cy(end) : end.y + INSET, tight ? cy(end) : bottom(end) - INSET);
}

function legsOf(ctx: Search, xs: readonly number[], a: EdgeRect, start: Exit, steps: readonly Step[], at: readonly number[], b: EdgeRect, end: Exit): Leg[] {
  const legs: Leg[] = [];
  for (const piece of [start.piece, end.piece]) if (piece) ctx.usage.set(piece, (ctx.usage.get(piece) ?? 0) + 1);
  if (start.side === 'left' || start.side === 'right') legs.push(stub(a));
  let run = leg('v', xs[at[0]], start.span.lo, start.span.hi);
  steps.forEach((step, s) => {
    const x = xs[at[s + 1]];
    if ('pass' in step) {
      const piece = spanAt(ctx.grid.rows[step.pass].pieces, x);
      if (!piece) return;
      run.lo = Math.max(run.lo, piece.lo);
      run.hi = Math.min(run.hi, piece.hi);
      ctx.usage.set(piece, (ctx.usage.get(piece) ?? 0) + 1);
    } else if (at[s + 1] !== at[s]) {
      legs.push(run, leg('h', 0, 0, 0, step.turn));
      run = leg('v', x, -Infinity, Infinity);
    } else run.across.push(step.turn);
  });
  run.lo = Math.max(run.lo, end.span.lo);
  run.hi = Math.min(run.hi, end.span.hi);
  legs.push(run);
  if (end.side === 'left' || end.side === 'right') legs.push(stub(b));
  for (const l of legs) {
    if (l.axis !== 'v') continue;
    if (!(l.lo <= l.want && l.want <= l.hi)) [l.lo, l.hi] = [l.want, l.want];
  }
  return legs;
}

/** The lanes one line chooses among: every lane of the picture, and the places its own ends would like. */
function lanesFor(grid: EdgeGrid, extra: readonly number[]): number[] {
  return [...new Set([...grid.lanes, ...extra])].sort((p, q) => p - q);
}

/** From `a` down to `b`, which is below it: across every row between, by a clear column of each. */
function descend(ctx: Search, a: EdgeRect, b: EdgeRect, sides: boolean): { legs: Leg[]; from: EdgeSide; to: EdgeSide } | undefined {
  const { grid } = ctx;
  const ra = reach(grid, a, 1);
  const rb = reach(grid, b, -1);
  if (ra.corridor > rb.corridor) return undefined;
  // One straight line where a column is clear from one end to the other.
  const aligned: number[] = [];
  if (!sides) for (const s of ra.through.length ? ra.through : ra.straight) for (const t of clip(rb.through.length ? rb.through : rb.straight, s)) aligned.push(mid(t));
  const xs = lanesFor(grid, [ra.prefer, rb.prefer, cx(a), cx(b), ...aligned]);
  const mode = sides ? { straight: false, beside: true, besideCost: 0 } : { straight: true, beside: true, besideCost: SIDE_EXIT };
  const start = exits(ra, xs, mode, ctx.usage);
  const end = exits(rb, xs, mode, ctx.usage);
  const steps: Step[] = [{ turn: ra.corridor }];
  for (let k = ra.corridor; k < rb.corridor; k++) steps.push({ pass: k }, { turn: k + 1 });
  const found = search(ctx, xs, start, steps, end);
  if (!found) return undefined;
  const [first, last] = [start[found.at[0]]!, end[found.at[steps.length]]!];
  return { legs: legsOf(ctx, xs, a, first, steps, found.at, b, last), from: first.side, to: last.side };
}

/** Straight across from one side to the other's facing side, when they stand side by side with nothing between. */
function across(grid: EdgeGrid, a: EdgeRect, b: EdgeRect): { legs: Leg[]; from: EdgeSide; to: EdgeSide } | undefined {
  const rightward = right(a) <= b.x;
  if (!rightward && right(b) > a.x) return undefined;
  const [l, r] = rightward ? [a, b] : [b, a];
  const shared: Span = { lo: Math.max(a.y, b.y), hi: Math.min(bottom(a), bottom(b)) };
  if (shared.hi - shared.lo < 2 * CLEAR) return undefined;
  const inset = Math.min(INSET, (shared.hi - shared.lo) / 2 - 1);
  let free: Span[] = [{ lo: shared.lo + inset, hi: shared.hi - inset }];
  for (const o of grid.obstacles) if (o.x < r.x && right(o) > right(l) && !holds(a, o) && !holds(b, o)) free = cut(free, o.y - CLEAR, bottom(o) + CLEAR);
  const clear = widest(free);
  if (!clear) return undefined;
  return { legs: [leg('h', mid(clear), clear.lo, clear.hi)], from: rightward ? 'right' : 'left', to: rightward ? 'left' : 'right' };
}

/** Two things in one band with something between them: out of a side of each, and round by a corridor above or below. */
function around(outer: Search, a: EdgeRect, b: EdgeRect): { legs: Leg[]; from: EdgeSide; to: EdgeSide } | undefined {
  const ctx: Search = { ...outer, roomy: !outer.labelled };
  const { grid } = ctx;
  const band = bandOf(grid.bands, a);
  const firstRow = grid.rows.findIndex((r) => r.band === band);
  let lastRow = firstRow;
  while (grid.rows[lastRow + 1]?.band === band) lastRow++;
  const level = (r: EdgeRect) => grid.levels[band] + cy(r) - grid.bands[band].y;
  const mode = { straight: false, beside: true, besideCost: 0 };
  let best: { cost: number; legs: () => Leg[]; from: EdgeSide; to: EdgeSide } | undefined;
  for (const dir of [1, -1] as const) {
    const ra = reach(grid, a, dir);
    const rb = reach(grid, b, dir);
    const xs = lanesFor(grid, [cx(a), cx(b)]);
    const start = exits(ra, xs, mode, ctx.usage);
    const end = exits(rb, xs, mode, ctx.usage);
    // The corridors it may turn in, nearest first. With a label, only one between bands, where the label has room.
    const nearest = dir > 0 ? Math.max(ra.corridor, rb.corridor) : Math.min(ra.corridor, rb.corridor);
    const furthest = dir > 0 ? lastRow + 1 : firstRow;
    const turns: number[] = [];
    for (let k = ctx.labelled ? furthest : nearest; dir > 0 ? k <= furthest : k >= furthest; k += dir) turns.push(k);
    for (const k of turns) {
      const steps: Step[] = [{ turn: ra.corridor, must: ra.corridor === k }];
      if (dir > 0) {
        for (let c = ra.corridor; c < k; c++) steps.push({ pass: c }, { turn: c + 1, must: c + 1 === k });
        for (let c = k - 1; c >= rb.corridor; c--) steps.push({ pass: c }, { turn: c });
      } else {
        for (let c = ra.corridor - 1; c >= k; c--) steps.push({ pass: c }, { turn: c, must: c === k });
        for (let c = k; c < rb.corridor; c++) steps.push({ pass: c }, { turn: c + 1 });
      }
      const found = search(ctx, xs, start, steps, end);
      if (!found) continue;
      const cost = found.cost + Math.abs(level(a) - grid.corridors[k].level) + Math.abs(level(b) - grid.corridors[k].level);
      if (best && cost >= best.cost) continue;
      const [first, last] = [start[found.at[0]]!, end[found.at[steps.length]]!];
      best = { cost, legs: () => legsOf(ctx, xs, a, first, steps, found.at, b, last), from: first.side, to: last.side };
    }
  }
  return best ? { legs: best.legs(), from: best.from, to: best.to } : undefined;
}

function plan(ctx: Search, req: EdgeRequest): Plan {
  const { grid } = ctx;
  const { from: a, to: b } = req;
  const [bandA, bandB] = [bandOf(grid.bands, a), bandOf(grid.bands, b)];
  const backwards = (way: { legs: Leg[]; from: EdgeSide; to: EdgeSide } | undefined) => {
    if (!way) return undefined;
    way.legs.reverse();
    for (const l of way.legs) l.across.reverse();
    return { legs: way.legs, from: way.to, to: way.from };
  };
  let way: { legs: Leg[]; from: EdgeSide; to: EdgeSide } | undefined;
  if (bandA !== bandB) way = bandA < bandB ? descend(ctx, a, b, false) : backwards(descend(ctx, b, a, false));
  else {
    way = across(grid, a, b);
    // One above the other in a band whose groups wrap: out of a side of each, and down the column beside them.
    // With no label to find room for, straight down from one to the other when that is clear.
    if (!way && bottom(a) <= b.y) way = descend(ctx, a, b, ctx.labelled);
    if (!way && bottom(b) <= a.y) way = backwards(descend(ctx, b, a, ctx.labelled));
    way ??= around(ctx, a, b);
  }
  // Nothing found (two ends on top of each other): the old straight answer, so there is a line.
  way ??= bottom(a) <= b.y ? { legs: [leg('v', cx(a), cx(a), cx(a))], from: 'bottom', to: 'top' } : bottom(b) <= a.y ? { legs: [leg('v', cx(a), cx(a), cx(a))], from: 'top', to: 'bottom' } : { legs: [leg('h', cy(a), cy(a), cy(a))], from: cx(b) >= cx(a) ? 'right' : 'left', to: cx(b) >= cx(a) ? 'left' : 'right' };
  way.legs[0].ports.push(`${req.fromId}|${way.from}`);
  way.legs[way.legs.length - 1].ports.push(`${req.toId}|${way.to}`);
  return { req, ...way };
}

// ── Keeping lines off each other ───────────────────────────────────────

interface Slot extends Span {
  want: number;
  /** Among slots that want the same place: the lower goes first. */
  order: number;
  id: string;
  pos: number;
}

/**
 * Give every slot a place as near as it can be to the one it wants, in the
 * order they want them, with `apart(a, b)` between any two that would
 * otherwise lie on each other (0 for two that cannot), and inside its own
 * bounds. A crowd that wants one place ends up centred on it; a crowd with
 * less room than it needs stands closer together, each still in a place of
 * its own.
 */
function settle<T extends Slot>(slots: readonly T[], apart: (a: T, b: T) => number): void {
  const order = [...slots].sort((a, b) => a.want - b.want || a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const n = order.length;
  // Only two whose bounds let them come that near can be in each other's way.
  const sep: number[][] = order.map((a, i) =>
    order.map((b, j) => {
      if (i >= j) return 0;
      const gap = apart(a, b);
      return gap > 0 && b.lo - a.hi < gap && a.lo - b.hi < gap ? gap : 0;
    }),
  );
  const crowd = order.map((_, i) => i);
  const find = (i: number): number => (crowd[i] === i ? i : (crowd[i] = find(crowd[i])));
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) if (sep[i][j] > 0) crowd[find(j)] = find(i);
  const crowds = new Map<number, number[]>();
  for (let i = 0; i < n; i++) (crowds.get(find(i)) ?? crowds.set(find(i), []).get(find(i))!).push(i);

  for (const members of crowds.values()) {
    /** Everyone's place with `scale` of the room asked for between them, or nothing when even that does not fit. */
    const place = (scale: number): number[] | undefined => {
      const m = members.length;
      const gap = (i: number, j: number) => sep[members[i]][members[j]] * scale;
      // Pushed apart to the right, pushed apart to the left, and half way between: what holds for both holds for the middle.
      const fwd = members.map((at) => order[at].want);
      for (let i = 0; i < m; i++) for (let j = 0; j < i; j++) if (gap(j, i) > 0) fwd[i] = Math.max(fwd[i], fwd[j] + gap(j, i));
      const bwd = members.map((at) => order[at].want);
      for (let i = m - 1; i >= 0; i--) for (let j = m - 1; j > i; j--) if (gap(i, j) > 0) bwd[i] = Math.min(bwd[i], bwd[j] - gap(i, j));
      const pos = members.map((_, i) => (fwd[i] + bwd[i]) / 2);
      // Then inside each one's bounds: up to its least, then down to its most.
      for (let i = 0; i < m; i++) {
        pos[i] = Math.max(pos[i], order[members[i]].lo);
        for (let j = 0; j < i; j++) if (gap(j, i) > 0) pos[i] = Math.max(pos[i], pos[j] + gap(j, i));
      }
      for (let i = m - 1; i >= 0; i--) {
        pos[i] = Math.min(pos[i], order[members[i]].hi);
        for (let j = m - 1; j > i; j--) if (gap(i, j) > 0) pos[i] = Math.min(pos[i], pos[j] - gap(i, j));
      }
      return pos.every((p, i) => p >= order[members[i]].lo - 1e-6) ? pos : undefined;
    };
    let pos = place(1);
    if (!pos) {
      // More than fit: the most of the room asked for that does.
      let [least, most] = [0, 1];
      pos = place(0)!;
      for (let round = 0; round < 16; round++) {
        const scale = (least + most) / 2;
        const fits = place(scale);
        if (fits) [pos, least] = [fits, scale];
        else most = scale;
      }
    }
    members.forEach((at, i) => (order[at].pos = pos![i]));
  }
}

/**
 * The ends on one side of the same thing stand in the order of where their
 * lines go, whatever place each would have liked: the places they wanted are
 * dealt out again in that order, so no line crosses its neighbour on the way out.
 */
function inOrderOfGoing(slots: ReadonlyArray<Slot & { leg: Leg }>): void {
  const bySide = new Map<string, Array<Slot & { leg: Leg }>>();
  for (const slot of slots) for (const port of slot.leg.ports) (bySide.get(port) ?? bySide.set(port, []).get(port)!).push(slot);
  for (const port of [...bySide.keys()].sort()) {
    const sharing = bySide.get(port)!;
    if (sharing.length < 2) continue;
    const wants = sharing.map((slot) => slot.want).sort((a, b) => a - b);
    [...sharing].sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : 1)).forEach((slot, i) => (slot.want = Math.min(Math.max(wants[i], slot.lo), slot.hi)));
  }
}

/** A run along a corridor, or the place a label takes on a line that goes straight across one. */
interface Run {
  id: string;
  plan: Plan;
  leg?: Leg;
  /** The line itself, and with its label. */
  lo: number;
  hi: number;
  from: number;
  to: number;
  /** Each end, and whether the line goes up (-1) or down (1) from there. */
  ends: Array<{ x: number; dir: -1 | 1 }>;
  track: number;
  /** Where its track is. */
  y: number;
  /** The middle of the label it carries. */
  label?: number;
}

/** How many times two runs' lines cross with `a` on the track above `b`. */
function crossings(a: Run, b: Run): number {
  let n = 0;
  for (const e of a.ends) if (e.dir > 0 && e.x > b.lo && e.x < b.hi) n++;
  for (const e of b.ends) if (e.dir < 0 && e.x > a.lo && e.x < a.hi) n++;
  return n;
}

/**
 * A track for every run in one corridor. Two that would touch never share
 * one; of two whose lines would cross less one way up than the other, that
 * way up is kept; and otherwise a run takes the first track free, so a strip
 * is no taller than it has to be. Returns how many tracks.
 */
function tracks(runs: Run[]): number {
  const touch = (a: Run, b: Run) => a.from < b.to + TRACK_GAP && b.from < a.to + TRACK_GAP;
  /** Whether `a` belongs above `b`: only where it saves a crossing. */
  const above = (a: Run, b: Run) => touch(a, b) && crossings(a, b) < crossings(b, a);
  // The runs that turn, in the order with the fewest crossings; then the labels of lines going straight across, which cross whatever they are given.
  const base = [...runs].sort((a, b) => a.lo - b.lo || a.hi - b.hi || (a.id < b.id ? -1 : 1));
  const order: Run[] = [];
  for (const run of base.filter((r) => r.ends.length)) {
    let best = 0;
    let bestCost = Infinity;
    for (let at = 0; at <= order.length; at++) {
      let cost = 0;
      order.forEach((other, i) => (cost += i < at ? crossings(other, run) : crossings(run, other)));
      if (cost < bestCost) [best, bestCost] = [at, cost];
    }
    order.splice(best, 0, run);
  }
  order.push(...base.filter((r) => !r.ends.length));
  let count = 0;
  order.forEach((run, i) => {
    const before = order.slice(0, i);
    let track = 0;
    for (const other of before) if (above(other, run)) track = Math.max(track, other.track + 1);
    while (before.some((other) => other.track === track && touch(other, run))) track++;
    run.track = track;
    count = Math.max(count, track + 1);
  });
  // One with tracks to spare sits nearest the middle of the strip.
  const middle = (count - 1) / 2;
  for (const run of order) {
    let lo = 0;
    let hi = count - 1;
    for (const other of order) {
      if (other === run) continue;
      if (above(other, run)) lo = Math.max(lo, other.track + 1);
      if (above(run, other)) hi = Math.min(hi, other.track - 1);
    }
    let best = run.track;
    for (let track = lo; track <= hi; track++) {
      if (order.some((other) => other !== run && other.track === track && touch(other, run))) continue;
      if (Math.abs(track - middle) < Math.abs(best - middle)) best = track;
    }
    run.track = best;
  }
  return count;
}

// ── Labels ─────────────────────────────────────────────────────────────

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

const overlap = (a: Box, b: Box, pad: number) => Math.max(0, Math.min(a.x + a.width, b.x + b.width + pad) - Math.max(a.x, b.x - pad)) * Math.max(0, Math.min(a.y + a.height, b.y + b.height + pad) - Math.max(a.y, b.y - pad));
const boxOf = (l: Pick<EdgeLabel, 'x' | 'y' | 'width' | 'height'>): Box => ({ x: l.x - l.width / 2, y: l.y - l.height / 2, width: l.width, height: l.height });

/**
 * A label for a line with no run in a strip (both ends in one band): on its
 * longest horizontal stretch when the words fit there; else on its longest
 * vertical one, level, and moved sideways off whatever card is beside it;
 * else the small label; and never on a card, a heading or another label.
 */
function looseLabel(points: readonly EdgePoint[], size: EdgeSize, compact: EdgeSize, grid: EdgeGrid, taken: readonly Box[]): EdgeLabel {
  const flat: Array<{ lo: number; hi: number; at: number }> = [];
  const upright: Array<{ lo: number; hi: number; at: number }> = [];
  for (let i = 1; i < points.length; i++) {
    const [p, q] = [points[i - 1], points[i]];
    if (p.y === q.y && p.x !== q.x) flat.push({ lo: Math.min(p.x, q.x), hi: Math.max(p.x, q.x), at: p.y });
    else if (p.x === q.x && p.y !== q.y) upright.push({ lo: Math.min(p.y, q.y), hi: Math.max(p.y, q.y), at: p.x });
  }
  flat.sort((a, b) => b.hi - b.lo - (a.hi - a.lo));
  upright.sort((a, b) => b.hi - b.lo - (a.hi - a.lo));
  const tries: EdgeLabel[] = [];
  const full = (x: number, y: number) => tries.push({ x, y, ...size, compact: false });
  const small = (x: number, y: number) => tries.push({ x, y, ...compact, compact: true });
  for (const s of flat) if (s.hi - s.lo >= size.width + 2 * SHOW) full((s.lo + s.hi) / 2, s.at);
  for (const s of flat) if (s.hi - s.lo >= compact.width + 2 * SHOW) small((s.lo + s.hi) / 2, s.at);
  for (const [put, box] of [
    [full, size],
    [small, compact],
  ] as const) {
    for (const s of upright) {
      const room = (s.hi - s.lo - box.height) / 2 - CLEAR;
      if (room < 0) continue;
      for (let up = 0; up <= room; up += box.height + 2) {
        for (const y of up ? [(s.lo + s.hi) / 2 - up, (s.lo + s.hi) / 2 + up] : [(s.lo + s.hi) / 2]) {
          // Level on the line, then sideways, a little at a time, with the line still under it.
          for (let shift = 0; shift <= box.width / 2 - EDGE; shift += 2) for (const x of shift ? [s.at + shift, s.at - shift] : [s.at]) put(x, y);
        }
      }
    }
  }
  // A line too short for even the small one: just above it, then just below.
  for (const s of flat) for (const dy of [-1, 1]) small((s.lo + s.hi) / 2, s.at + dy * (compact.height / 2 + CLEAR + 1));
  const middle = points[Math.floor(points.length / 2)] ?? { x: 0, y: 0 };
  small(flat[0] ? (flat[0].lo + flat[0].hi) / 2 : middle.x, flat[0]?.at ?? middle.y);

  let best = tries[0];
  let bestCost = Infinity;
  for (const t of tries) {
    const box = boxOf(t);
    let cost = Math.max(0, EDGE - box.x) + Math.max(0, box.x + box.width - (grid.width - EDGE));
    for (const o of grid.obstacles) cost += overlap(box, o, 1);
    for (const o of taken) cost += overlap(box, o, 2);
    if (cost < bestCost) [best, bestCost] = [t, cost];
    if (cost === 0) break;
  }
  return best;
}

// ── Routing ────────────────────────────────────────────────────────────

/** A name for leg `i` of line `at` that sorts in the order the lines were asked for. */
const legId = (at: number, i: number | string) => `${String(at).padStart(6, '0')}:${typeof i === 'number' ? String(i).padStart(3, '0') : i}`;

/** Route every line over one picture's lanes (`edgeGrid`). */
export function routeEdges(requests: readonly EdgeRequest[], grid: EdgeGrid, options: RouteOptions = {}): EdgeRouting {
  const laneStep = options.laneStep ?? LANE_STEP;
  const compact = options.compact ?? COMPACT;
  const bias = options.bias ?? 0;
  const usage = new Map<Piece, number>();
  const plans = requests.map((req) => plan({ grid, labelled: !!req.label, usage }, req));
  const zone = (k: number): Span => ({ lo: grid.corridors[k].top, hi: grid.corridors[k].bottom });
  const edgeY = (r: EdgeRect, side: EdgeSide) => (side === 'top' ? r.y : side === 'bottom' ? bottom(r) : cy(r));
  const edgeX = (r: EdgeRect, side: EdgeSide) => (side === 'left' ? r.x : side === 'right' ? right(r) : cx(r));

  // 1. Columns: every vertical leg, the ones sharing a side of an end an arrowhead apart, the ones sharing a column a line apart.
  interface LegSlot extends Slot {
    leg: Leg;
  }
  const columns: LegSlot[] = [];
  plans.forEach((p, at) => {
    p.legs.forEach((l, i) => {
      if (l.axis !== 'v') return;
      const [prev, next] = [p.legs[i - 1], p.legs[i + 1]];
      // How far it reaches, at most: to the far side of the corridor it turns in.
      const reachOf = (other: Leg | undefined, end: 'from' | 'to'): Span => {
        if (!other) return { lo: edgeY(p.req[end], p[end]), hi: edgeY(p.req[end], p[end]) };
        return other.corridor !== undefined ? zone(other.corridor) : { lo: other.lo, hi: other.hi };
      };
      const [up, down] = [reachOf(prev, 'from'), reachOf(next, 'to')];
      l.span = { lo: Math.min(up.lo, down.lo), hi: Math.max(up.hi, down.hi) };
      // Where it goes on to, either end: what orders the lines that share a column or a side.
      const onward = (other: Leg | undefined, beyond: Leg | undefined, end: 'from' | 'to') => (!other ? l.want : beyond ? beyond.want : edgeX(p.req[end], p[end]));
      l.far = (onward(prev, p.legs[i - 2], 'from') + onward(next, p.legs[i + 2], 'to')) / 2;
      const want = Math.min(Math.max(l.want + bias, l.lo), l.hi);
      columns.push({ leg: l, want, lo: l.lo, hi: l.hi, order: l.far, id: legId(at, i), pos: want });
    });
  });
  const sharesPort = (a: Leg, b: Leg) => a.ports.some((port) => b.ports.includes(port));
  inOrderOfGoing(columns);
  settle(columns, (a, b) => (sharesPort(a.leg, b.leg) ? FAN_STEP : a.leg.span.lo < b.leg.span.hi - 1 && b.leg.span.lo < a.leg.span.hi - 1 ? STEP : 0));
  for (const c of columns) c.leg.pos = c.pos;

  // 2. The runs out of the side of an end (and straight across between two): the same, up and down the side.
  interface StubSlot extends LegSlot {
    reach: Span;
  }
  const stubs: StubSlot[] = [];
  plans.forEach((p, at) => {
    p.legs.forEach((l, i) => {
      if (l.axis !== 'h' || l.corridor !== undefined) return;
      const [prev, next] = [p.legs[i - 1], p.legs[i + 1]];
      const x0 = prev ? prev.pos : edgeX(p.req.from, p.from);
      const x1 = next ? next.pos : edgeX(p.req.to, p.to);
      // The column it joins leads up or down: the ones going up sit above the ones going down.
      const column = prev ?? next;
      const beyond = prev ? p.legs[i - 2] : p.legs[i + 2];
      l.far = !column ? l.want : !beyond ? (prev ? edgeY(p.req.from, p.from) : edgeY(p.req.to, p.to)) : beyond.corridor !== undefined ? mid(zone(beyond.corridor)) : beyond.want;
      const want = Math.min(Math.max(l.want + bias, l.lo), l.hi);
      stubs.push({ leg: l, want, lo: l.lo, hi: l.hi, order: l.far, id: legId(at, i), pos: want, reach: { lo: Math.min(x0, x1), hi: Math.max(x0, x1) } });
    });
  });
  inOrderOfGoing(stubs);
  settle(stubs, (a, b) => (sharesPort(a.leg, b.leg) ? FAN_STEP : a.reach.lo < b.reach.hi && b.reach.lo < a.reach.hi ? STEP : 0));
  for (const s of stubs) s.leg.pos = s.pos;

  // 3. Tracks: every run along a corridor, and the label of every line that has a strip to put it in.
  const byCorridor = new Map<number, Run[]>();
  const add = (k: number, run: Run) => (byCorridor.get(k) ?? byCorridor.set(k, []).get(k)!).push(run);
  const labelRun = new Map<Plan, Run>();
  const roomy = (k: number) => grid.corridors[k].kind !== 'inner';
  plans.forEach((p, at) => {
    const mine: Run[] = [];
    p.legs.forEach((l, i) => {
      if (l.axis !== 'h' || l.corridor === undefined) return;
      const here = mid(zone(l.corridor));
      const end = (column: Leg, beyond: Leg | undefined, side: 'from' | 'to'): { x: number; dir: -1 | 1 } => {
        const y = !beyond ? edgeY(p.req[side], p[side]) : beyond.corridor !== undefined ? mid(zone(beyond.corridor)) : beyond.pos;
        return { x: column.pos, dir: y < here ? -1 : 1 };
      };
      const ends = [end(p.legs[i - 1], p.legs[i - 2], 'from'), end(p.legs[i + 1], p.legs[i + 2], 'to')];
      const [lo, hi] = [Math.min(ends[0].x, ends[1].x), Math.max(ends[0].x, ends[1].x)];
      const run: Run = { id: legId(at, i), plan: p, leg: l, lo, hi, from: lo, to: hi, ends, track: 0, y: 0 };
      add(l.corridor, run);
      if (roomy(l.corridor)) mine.push(run);
    });
    const size = p.req.label;
    if (!size) return;
    // The label rides the longest run in a strip. A line that goes straight across its strips takes a place in the first.
    let host: Run | undefined = mine[0];
    for (const run of mine) if (run.hi - run.lo > host!.hi - host!.lo) host = run;
    let k = host?.leg?.corridor;
    if (!host) {
      for (const l of p.legs) {
        k = l.axis === 'v' ? l.across.find(roomy) : undefined;
        if (k === undefined) continue;
        host = { id: legId(at, 'label'), plan: p, lo: l.pos, hi: l.pos, from: l.pos, to: l.pos, ends: [], track: 0, y: 0 };
        add(k, host);
        break;
      }
    }
    if (!host || k === undefined) return;
    const strip = zone(k);
    // Along its run, as near the middle as leaves it clear of the other lines coming down through the strip: anywhere on a run
    // longer than it is, and on a shorter one (or a line going straight across) as far as still leaves the line under it.
    const half = size.width / 2;
    const middle = (host.lo + host.hi) / 2;
    const slack = Math.max(0, Math.abs((host.hi - host.lo) / 2 - half) - INSET);
    const others = columns.filter((c) => !p.legs.includes(c.leg) && c.leg.span.lo < strip.hi && c.leg.span.hi > strip.lo).map((c) => c.pos);
    const hits = (x: number) => others.filter((o) => Math.abs(o - x) < half + 2).length;
    let x = middle;
    let fewest = hits(middle);
    for (let shift = 6; shift <= slack && fewest > 0; shift += 6) {
      for (const candidate of [middle - shift, middle + shift]) {
        if (hits(candidate) < fewest) [x, fewest] = [candidate, hits(candidate)];
      }
    }
    x = Math.min(Math.max(x, half + EDGE), Math.max(half + EDGE, grid.width - half - EDGE));
    host.label = x;
    host.from = Math.min(host.from, x - half);
    host.to = Math.max(host.to, x + half);
    labelRun.set(p, host);
  });

  const loads = new Array<number>(grid.bands.length + 1).fill(0);
  for (const [k, runs] of byCorridor) {
    const corridor = grid.corridors[k];
    const count = tracks(runs);
    if (corridor.gap >= 0) loads[corridor.gap] = Math.max(loads[corridor.gap], count);
    for (const run of runs) {
      let y: number;
      if (corridor.kind === 'top') y = corridor.bottom - EDGE_GAP - (count - 1 - run.track) * laneStep;
      else if (corridor.kind === 'bottom') y = corridor.top + EDGE_GAP + run.track * laneStep;
      else {
        const step = corridor.kind === 'strip' ? laneStep : count > 1 ? Math.min(STEP, (corridor.bottom - corridor.top) / (count - 1)) : 0;
        y = (corridor.top + corridor.bottom) / 2 + (run.track - (count - 1) / 2) * step;
      }
      run.y = y + bias;
      if (run.leg) run.leg.pos = run.y;
    }
  }

  // 4. The lines themselves.
  const routes = new Map<string, EdgeRoute>();
  const labels: Box[] = [];
  const loose: Plan[] = [];
  for (const p of plans) {
    const { legs } = p;
    const first = legs[0];
    const last = legs[legs.length - 1];
    const points: EdgePoint[] = [first.axis === 'v' ? { x: first.pos, y: edgeY(p.req.from, p.from) } : { x: edgeX(p.req.from, p.from), y: first.pos }];
    for (let i = 0; i + 1 < legs.length; i++) points.push(legs[i].axis === 'v' ? { x: legs[i].pos, y: legs[i + 1].pos } : { x: legs[i + 1].pos, y: legs[i].pos });
    points.push(last.axis === 'v' ? { x: last.pos, y: edgeY(p.req.to, p.to) } : { x: edgeX(p.req.to, p.to), y: last.pos });
    const route: EdgeRoute = { id: p.req.id, d: roundedPath(points), points, from: p.from, to: p.to };
    const host = labelRun.get(p);
    if (host && p.req.label) {
      route.label = { x: host.label ?? (host.lo + host.hi) / 2, y: host.y, ...p.req.label, compact: false };
      labels.push(boxOf(route.label));
    } else if (p.req.label) loose.push(p);
    routes.set(p.req.id, route);
  }

  // 5. The labels with no strip of their own, each clear of the ones before it.
  for (const p of loose) {
    const route = routes.get(p.req.id)!;
    route.label = looseLabel(route.points, p.req.label!, compact, grid, labels);
    labels.push(boxOf(route.label));
  }
  return { routes, loads };
}

/** The room a strip needs for `lanes` tracks of `laneStep` each, and never less than `least`. */
export function stripHeight(lanes: number, laneStep: number, least: number): number {
  return Math.max(least, Math.ceil(lanes * laneStep + laneStep * 0.75));
}

/** The room the margin above the first band (or below the last) needs for `lanes` tracks, and never less than `least`. */
export function marginHeight(lanes: number, laneStep: number, least: number): number {
  return lanes ? Math.max(least, Math.ceil(EDGE_GAP + (lanes - 1) * laneStep + laneStep * 0.75)) : least;
}
